import React, { useState } from "react";
import {
  FiCheckCircle,
  FiAlertTriangle,
  FiClock,
  FiFileText,
  FiSend,
  FiUserCheck,
  FiShield
} from "react-icons/fi";
import type { RcaReviewData, RcaReviewChecklistItem } from "../../../types/rca.types.js";

export type RcaReviewDecisionType = "APPROVE" | "REJECT" | "REWORK";

export type RcaReviewApprovalProps = {
  reviewData?: RcaReviewData;
  onSubmitDecision?: (decision: RcaReviewDecisionType, comments: string) => void;
  readOnly?: boolean;
};

const INITIAL_CHECKLIST: RcaReviewChecklistItem[] = [
  { id: "chk-1", label: "Problem statement is clear & measurable", status: "Yes" },
  { id: "chk-2", label: "Evidence is sufficient and validated", status: "Yes" },
  { id: "chk-3", label: "Method analysis is complete", status: "Yes" },
  { id: "chk-4", label: "Root cause verified with test data", status: "Pending" },
  { id: "chk-5", label: "Actions are appropriate & assigned", status: "Overdue" },
  { id: "chk-6", label: "Risk & FMEA assessed", status: "Yes" },
];

export function RcaReviewApproval({
  reviewData = {
    reviewedBy: "Priya (Lead Quality Reviewer)",
    reviewLevel: "Level 1",
    reviewType: "Technical Review",
    reviewDate: "26-Aug-2026",
    checklist: INITIAL_CHECKLIST,
    comments: "More evidence required for root cause verification. Please add calibration test report #CMM-2026-081.",
    decision: "REWORK"
  },
  onSubmitDecision,
  readOnly = false,
}: RcaReviewApprovalProps) {
  const [activeTab, setActiveTab] = useState<"DETAILS" | "COMMENTS" | "DECISION" | "HISTORY">("DETAILS");
  const [checklist, setChecklist] = useState<RcaReviewChecklistItem[]>(reviewData.checklist || INITIAL_CHECKLIST);
  const [comments, setComments] = useState<string>(reviewData.comments || "");
  const [decision, setDecision] = useState<"APPROVE" | "REJECT" | "REWORK">(reviewData.decision || "REWORK");
  const [submitted, setSubmitted] = useState<boolean>(false);

  React.useEffect(() => {
    if (reviewData) {
      if (reviewData.checklist && reviewData.checklist.length > 0) {
        setChecklist(reviewData.checklist);
      }
      if (reviewData.comments !== undefined) {
        setComments(reviewData.comments);
      }
      if (reviewData.decision) {
        setDecision(reviewData.decision);
      }
    }
  }, [reviewData]);

  const getStatusTag = (status: RcaReviewChecklistItem["status"]) => {
    if (status === "Yes") {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
          Yes
        </span>
      );
    }
    if (status === "Pending") {
      return (
        <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
          Pending
        </span>
      );
    }
    return (
      <span className="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-50 text-rose-700 border border-rose-200">
        Overdue
      </span>
    );
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (readOnly) return;
    onSubmitDecision?.(decision, comments);
    setSubmitted(true);
  };

  return (
    <div className="space-y-5">
      {/* Top Header */}
      <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-xs font-semibold text-blue-600 uppercase tracking-wider">CMTS &gt; Formal Sign-Off</div>
          <h2 className="text-lg font-bold text-slate-900 mt-0.5">8. Review & Approval Workflow</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            6-point QA checklist verification and formal Approve / Rework / Reject workflow with Separation-of-Duties (SoD).
          </p>
        </div>

        <div className="flex items-center gap-2">
          <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200 flex items-center gap-1.5">
            <FiClock className="w-3.5 h-3.5" />
            Under Quality Review
          </span>
        </div>
      </div>

      {/* Main Review Form */}
      <form onSubmit={handleFormSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
        {/* Left 2 Cols: Checklist Table */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden space-y-4">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="text-sm font-bold text-slate-900">Review Checklist (6 Requirements)</h3>
            <span className="text-xs text-slate-500 font-semibold">Reviewer: {reviewData.reviewedBy}</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 text-slate-700 font-semibold border-b border-slate-200 uppercase text-[11px]">
                <tr>
                  <th className="py-3 px-4">#</th>
                  <th className="py-3 px-4">Checklist Item</th>
                  <th className="py-3 px-4 text-center">Status</th>
                  <th className="py-3 px-4 text-right">Verification</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {checklist.map((item, idx) => (
                  <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                    <td className="py-3 px-4 text-slate-400 font-bold">{idx + 1}</td>
                    <td className="py-3 px-4 font-semibold text-slate-800">{item.label}</td>
                    <td className="py-3 px-4 text-center">{getStatusTag(item.status)}</td>
                    <td className="py-3 px-4 text-right">
                      <select
                        disabled={readOnly}
                        value={item.status}
                        onChange={(e) => {
                          const updated = checklist.map(c => c.id === item.id ? { ...c, status: e.target.value as any } : c);
                          setChecklist(updated);
                        }}
                        className="text-xs p-1 rounded-md border border-slate-300 bg-white text-slate-700"
                      >
                        <option value="Yes">Yes</option>
                        <option value="Pending">Pending</option>
                        <option value="Overdue">Overdue</option>
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Right 1 Col: Decision & Comments */}
        <div className="bg-white p-5 rounded-xl border border-slate-200 shadow-sm space-y-4">
          <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wider flex items-center gap-2">
            <FiUserCheck className="w-4 h-4 text-blue-600" />
            Reviewer Decision
          </h3>

          <div className="space-y-2 text-xs">
            <label className="block text-slate-700 font-semibold">Decision:</label>
            <div className="grid grid-cols-3 gap-1.5">
              <button
                type="button"
                disabled={readOnly}
                onClick={() => setDecision("APPROVE")}
                className={`py-2 px-2 rounded-lg font-bold text-center border transition-all text-xs ${decision === "APPROVE"
                  ? "bg-emerald-600 text-white border-emerald-600 shadow-sm"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
              >
                Approve
              </button>
              <button
                type="button"
                disabled={readOnly}
                onClick={() => setDecision("REWORK")}
                className={`py-2 px-2 rounded-lg font-bold text-center border transition-all text-xs ${decision === "REWORK"
                  ? "bg-amber-500 text-white border-amber-500 shadow-sm"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
              >
                Rework
              </button>
              <button
                type="button"
                disabled={readOnly}
                onClick={() => setDecision("REJECT")}
                className={`py-2 px-2 rounded-lg font-bold text-center border transition-all text-xs ${decision === "REJECT"
                  ? "bg-rose-600 text-white border-rose-600 shadow-sm"
                  : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
                  }`}
              >
                Reject
              </button>
            </div>
          </div>

          <div className="space-y-1.5 text-xs">
            <label className="block text-slate-700 font-semibold">Review Remarks & Findings:</label>
            <textarea
              rows={4}
              disabled={readOnly}
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              className="w-full p-2.5 rounded-lg border border-slate-300 bg-white text-slate-900 focus:ring-2 focus:ring-blue-500 font-medium"
              placeholder="State rationale for approval, specific rework requirements, or rejection basis..."
            />
          </div>

          {!readOnly && (
            <button
              type="submit"
              className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold text-xs rounded-lg shadow-sm flex items-center justify-center gap-2 transition-all transform hover:-translate-y-0.5"
            >
              <FiSend className="w-3.5 h-3.5" />
              Submit Review Decision
            </button>
          )}

          {submitted && (
            <div className="p-3 bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-lg text-xs flex items-center gap-2">
              <FiCheckCircle className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Decision submitted successfully to audit record.</span>
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
